/* eslint-disable fp/no-mutation, fp/no-mutating-methods */
type CioMethod = 'identify' | 'load' | 'off' | 'on' | 'page' | 'sidentify' | 'track';
type CioQueue = Record<CioMethod, (...args: unknown[]) => void> & unknown[];
type MarketingScriptOutcome = 'error' | 'success';

export type MarketingDebugEvent = {
  at: number;
  detail?: Record<string, string>;
  name: string;
  timestamp: string;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    duckbillHistoryEventsInstalled?: boolean;
    duckbillLogLoadPerf?: (message: string) => void;
    duckbillMarketingDebugEvents?: MarketingDebugEvent[];
    duckbillMarketingHtmlBegin?: (name: string) => void;
    duckbillMarketingHtmlCompleted?: number;
    duckbillMarketingHtmlExpected?: number | null;
    duckbillMarketingHtmlFinished?: (name: string, outcome: MarketingScriptOutcome) => void;
    duckbillMarketingScriptsReleased?: boolean;
    duckbillMarketingSessionActive?: boolean;
    duckbillMarketingSessionBegin?: () => void;
    duckbillMarketingSessionStartedAt?: number | null;
    duckbillRecordMarketingDebugEvent?: (name: string, detail?: Record<string, string>) => void;
    duckbillTryMarketingLoadEnd?: () => void;
  }
}

const cioMethods: CioMethod[] = ['load', 'identify', 'sidentify', 'track', 'page', 'on', 'off'];

const installHistoryEvents = (browserWindow: Window): void => {
  if (browserWindow.duckbillHistoryEventsInstalled) {
    return;
  }

  const notifyLocationChange = () => {
    browserWindow.dispatchEvent(new Event('duckbill:locationchange'));
  };
  const wrapHistoryMethod = (method: 'pushState' | 'replaceState') => {
    const original = browserWindow.history[method].bind(browserWindow.history);
    browserWindow.history[method] = ((...args: Parameters<History['pushState']>) => {
      const result = original(...args);
      notifyLocationChange();
      return result;
    }) as History[typeof method];
  };

  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');
  browserWindow.duckbillHistoryEventsInstalled = true;
};

const getMarketingLoadDuration = (browserWindow: Window): number =>
  Math.round(
    browserWindow.performance.now() -
      (browserWindow.duckbillMarketingSessionStartedAt ?? browserWindow.performance.now()),
  );

const installDebugTimeline = (browserWindow: Window): void => {
  browserWindow.duckbillMarketingDebugEvents = browserWindow.duckbillMarketingDebugEvents ?? [];
  browserWindow.duckbillRecordMarketingDebugEvent = (name, detail) => {
    const event = {
      at: Math.round(browserWindow.performance.now()),
      detail,
      name,
      timestamp: new Date().toISOString(),
    };

    browserWindow.duckbillMarketingDebugEvents?.push(event);
    browserWindow.performance.mark?.(`duckbill:${name}`);
  };
};

const installCustomerIoStub = (browserWindow: Window): void => {
  const target = browserWindow as Window & { _cio?: CioQueue };
  const queue = (Array.isArray(target._cio) ? target._cio : []) as CioQueue;

  cioMethods.forEach((method) => {
    queue[method] = (...args: unknown[]) => {
      queue.push([method, ...args]);
    };
  });

  target._cio = queue;
};

const installGoogleTagManagerStub = (browserWindow: Window): void => {
  const target = browserWindow as Window & { dataLayer?: unknown[] };
  target.dataLayer = Array.isArray(target.dataLayer) ? target.dataLayer : [];
};

export const installMarketingScriptBootstrap = (browserWindow: Window = window): void => {
  installHistoryEvents(browserWindow);
  installDebugTimeline(browserWindow);
  installGoogleTagManagerStub(browserWindow);
  installCustomerIoStub(browserWindow);

  browserWindow.duckbillMarketingScriptsReleased = false;
  browserWindow.duckbillMarketingSessionActive = false;
  browserWindow.duckbillMarketingHtmlExpected = null;
  browserWindow.duckbillMarketingHtmlCompleted = 0;
  browserWindow.duckbillMarketingSessionStartedAt = null;

  browserWindow.duckbillLogLoadPerf = (message) => {
    browserWindow.duckbillRecordMarketingDebugEvent?.('load_log', { message });
    const browserConsole = (browserWindow as Window & { console?: Console }).console;
    if (browserConsole) {
      browserConsole.info(
        `[duckbill:load] ${new Date().toISOString()} (+${Math.round(browserWindow.performance.now())}ms) ${message}`,
      );
    }
  };

  browserWindow.duckbillTryMarketingLoadEnd = () => {
    const expected = browserWindow.duckbillMarketingHtmlExpected;
    if (!browserWindow.duckbillMarketingSessionActive) {
      return;
    }
    if (expected === null || typeof expected === 'undefined') {
      return;
    }
    if ((browserWindow.duckbillMarketingHtmlCompleted ?? 0) < expected) {
      return;
    }

    browserWindow.duckbillLogLoadPerf?.(
      `Marketing load end (+${getMarketingLoadDuration(browserWindow)}ms from begin)`,
    );
    browserWindow.duckbillRecordMarketingDebugEvent?.('marketing_load_end');
    browserWindow.duckbillMarketingSessionActive = false;
    browserWindow.duckbillMarketingSessionStartedAt = null;
  };

  browserWindow.duckbillMarketingSessionBegin = () => {
    if (browserWindow.duckbillMarketingSessionActive) {
      return;
    }

    browserWindow.duckbillMarketingSessionActive = true;
    browserWindow.duckbillMarketingSessionStartedAt = browserWindow.performance.now();
    browserWindow.duckbillMarketingHtmlExpected = null;
    browserWindow.duckbillMarketingHtmlCompleted = 0;
    browserWindow.duckbillLogLoadPerf?.('Marketing load begin');
    browserWindow.duckbillRecordMarketingDebugEvent?.('marketing_load_begin');
  };

  browserWindow.duckbillMarketingHtmlBegin = (name) => {
    browserWindow.duckbillLogLoadPerf?.(`Marketing script begin: ${name}`);
    browserWindow.duckbillRecordMarketingDebugEvent?.('marketing_script_inserted', { script: name });
  };

  browserWindow.duckbillMarketingHtmlFinished = (name, outcome) => {
    browserWindow.duckbillLogLoadPerf?.(`Marketing script end: ${name} (${outcome})`);
    browserWindow.duckbillRecordMarketingDebugEvent?.('marketing_script_finished', { outcome, script: name });
    browserWindow.duckbillMarketingHtmlCompleted = (browserWindow.duckbillMarketingHtmlCompleted ?? 0) + 1;
    browserWindow.duckbillTryMarketingLoadEnd?.();
  };

  browserWindow.duckbillLogLoadPerf('Marketing bootstrap installed');
  browserWindow.duckbillRecordMarketingDebugEvent?.('marketing_bootstrap_installed');
};
